import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ArticleFooter.module.css';
import { Icons } from './icons';

type ArticleFooterPlatform = {
  platform: 'facebook' | 'instagram' | 'x' | 'clipboard_link';
};

export type ArticleFooterProps = {
  socials: ArticleFooterPlatform[];
};

// Single source of truth for the page-template ARTICLE_FOOTER_V1, shared by RR7 atlas and atlas-astro.
// It reads `useTranslation(['label'])` for the per-platform share URLs (`CX_SOCIAL_<PLATFORM>_ACCOUNT`),
// so each app must render it inside an i18next provider: RR7 has one app-wide; atlas-astro wraps it with
// `withIsland` (i18n seed + ErrorBoundary + Suspense). The `clipboard_link` platform copies the page URL
// via `navigator.clipboard`, so the component is interactive and the Astro island must hydrate. SVGs are
// inlined so the component pulls no host asset imports.
const capitalizeWords = (str: string) => {
  return str
    .split('_')
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
};
export const ArticleFooter = (props: ArticleFooterProps) => {
  const { t } = useTranslation(['label']);
  const copyArticleUrlToClipboard = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const url = window.location.href;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        console.log('Link copied!', url);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
      });
  };
  const socialIcons = useMemo(() => {
    return props.socials.map((social, index) => {
      const url = t(`CX_SOCIAL_${social.platform.toUpperCase()}_ACCOUNT`);

      const Icon = Icons[capitalizeWords(social.platform) as keyof typeof Icons];
      return (
        <div key={index}>
          <a
            data-testid={`platform-${social.platform}`}
            href={url.length !== 0 && social.platform !== 'clipboard_link' ? url : '#'}
            onClick={social.platform === 'clipboard_link' ? copyArticleUrlToClipboard : undefined}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Icon />
          </a>
        </div>
      );
    });
  }, [props.socials, t]);
  return (
    <div className={styles.articleFooterWrapper} data-testid="article-footer-div">
      {socialIcons}
    </div>
  );
};
